import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Tag, Space, Typography, Button, Select, DatePicker, Input,
  Tooltip, Modal, Descriptions, Switch, Row, Col, Popconfirm, message
} from 'antd';
import {
  HistoryOutlined, ReloadOutlined, FilterOutlined, EyeOutlined,
  CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined,
  ExclamationCircleOutlined, DownloadOutlined, StopOutlined
} from '@ant-design/icons';
import axios from 'axios';
import logger from '../../services/logger';

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

/**
 * CommandHistoryTable Component
 *
 * Komut geçmişini filtreli ve sayfalı olarak gösterir.
 * Auto-refresh ve export özellikleri içerir.
 */
const CommandHistoryTable = ({ deviceId = null, autoRefresh = false }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    command_type: null,
    status: null,
    command: null,
    days: 7
  });
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(autoRefresh);

  useEffect(() => {
    fetchHistory();
  }, [pagination.current, pagination.pageSize, filters, deviceId]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefreshEnabled) return;

    const interval = setInterval(() => {
      fetchHistory(true);
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [autoRefreshEnabled, filters]);

  const fetchHistory = async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      const params = {
        page: pagination.current,
        page_size: pagination.pageSize,
        days: filters.days
      };

      if (deviceId) params.device_id = deviceId;
      if (filters.command_type) params.command_type = filters.command_type;
      if (filters.status) params.status = filters.status;
      if (filters.command) params.command = filters.command;

      const response = await axios.get('/api/v1/mqtt/command-history', { params });

      setHistory(response.data.items || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total
      }));
    } catch (error) {
      logger.error('Command history fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCommand = async (record) => {
    try {
      await axios.post(`/api/v1/mqtt/command-history/${record.command_id}/cancel`);
      message.success('Komut iptal edildi');
      fetchHistory(true);
    } catch (error) {
      logger.error('Command cancel error:', error);
      message.error(error.response?.data?.detail || 'Komut iptal edilemedi');
    }
  };

  // İptal edilebilir (bekleyen) durumlar
  const isCancellable = (status) => ['queued', 'pending', 'sent'].includes(status);

  const handleTableChange = (pag) => {
    setPagination({
      ...pagination,
      current: pag.current,
      pageSize: pag.pageSize
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'timeout':
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      case 'failed':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'pending':
      case 'sent':
        return <ExclamationCircleOutlined style={{ color: '#1890ff' }} />;
      case 'queued':
        return <ClockCircleOutlined style={{ color: '#722ed1' }} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'green';
      case 'timeout': return 'orange';
      case 'failed': return 'red';
      case 'pending': return 'blue';
      case 'sent': return 'cyan';
      case 'queued': return 'purple';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    // queued dışında mevcut ham gösterim korunur
    return status === 'queued' ? 'Kuyrukta (offline)' : status;
  };

  const columns = [
    {
      title: 'Zaman',
      dataIndex: 'sent_at',
      key: 'sent_at',
      width: 140,
      render: (time) => (
        <Text style={{ fontSize: '11px' }}>
          {new Date(time).toLocaleString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </Text>
      ),
    },
    {
      title: 'Cihaz',
      dataIndex: 'device_name',
      key: 'device_name',
      width: 120,
      ellipsis: true,
      render: (name, record) => (
        <Tooltip title={record.device_mac}>
          <Text strong style={{ fontSize: '12px' }}>{name || `ID: ${record.device_id}`}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Tip',
      dataIndex: 'command_type',
      key: 'command_type',
      width: 80,
      render: (type) => (
        <Tag color={type === 'control' ? 'blue' : 'green'} size="small">
          {type === 'control' ? 'Control' : 'Config'}
        </Tag>
      ),
    },
    {
      title: 'Komut',
      dataIndex: 'command',
      key: 'command',
      width: 100,
      render: (cmd) => <Text code style={{ fontSize: '11px' }}>{cmd}</Text>,
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Space size="small">
          {getStatusIcon(status)}
          <Tag color={getStatusColor(status)} size="small">
            {getStatusText(status)}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Kullanıcı',
      dataIndex: 'username',
      key: 'username',
      width: 100,
      render: (username) => <Text type="secondary" style={{ fontSize: '11px' }}>{username || '-'}</Text>,
    },
    {
      title: 'Süre',
      key: 'duration',
      width: 70,
      render: (_, record) => {
        if (!record.completed_at || !record.sent_at) return '-';
        const duration = (new Date(record.completed_at) - new Date(record.sent_at)) / 1000;
        return <Text type="secondary" style={{ fontSize: '11px' }}>{duration.toFixed(1)}s</Text>;
      },
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Space size={0}>
          <Tooltip title="Detay">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => {
                setSelectedRecord(record);
                setDetailModalVisible(true);
              }}
            />
          </Tooltip>
          {isCancellable(record.status) && (
            <Popconfirm
              title="Komutu iptal et"
              description={
                record.status === 'queued'
                  ? 'Bu komut kuyruktan çıkarılacak ve cihaz online olsa bile gönderilmeyecek.'
                  : 'Bekleyen komut iptal edilecek (cihaza iletilmiş olabilir).'
              }
              okText="İptal Et"
              cancelText="Vazgeç"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleCancelCommand(record)}
            >
              <Tooltip title="İptal et">
                <Button type="text" danger icon={<StopOutlined />} size="small" />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const exportToCsv = () => {
    const headers = ['Zaman', 'Cihaz', 'MAC', 'Tip', 'Komut', 'Durum', 'Kullanıcı', 'Hata'];
    const rows = history.map(h => [
      new Date(h.sent_at).toISOString(),
      h.device_name,
      h.device_mac,
      h.command_type,
      h.command,
      h.status,
      h.username,
      h.error_message || ''
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `command_history_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <Card
      title={
        <Space>
          <HistoryOutlined />
          <span>Komut Geçmişi</span>
          <Tag>{pagination.total} kayıt</Tag>
        </Space>
      }
      extra={
        <Space>
          <Text style={{ fontSize: '11px' }}>Auto-refresh:</Text>
          <Switch
            size="small"
            checked={autoRefreshEnabled}
            onChange={setAutoRefreshEnabled}
          />
          <Button
            icon={<DownloadOutlined />}
            onClick={exportToCsv}
            size="small"
            disabled={history.length === 0}
          >
            Export
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchHistory()}
            loading={loading}
            size="small"
          />
        </Space>
      }
    >
      {/* Filters */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={4}>
          <Select
            placeholder="Komut Tipi"
            allowClear
            style={{ width: '100%' }}
            value={filters.command_type}
            onChange={(v) => handleFilterChange('command_type', v)}
            size="small"
          >
            <Option value="control">Control</Option>
            <Option value="config">Config</Option>
          </Select>
        </Col>
        <Col span={4}>
          <Select
            placeholder="Durum"
            allowClear
            style={{ width: '100%' }}
            value={filters.status}
            onChange={(v) => handleFilterChange('status', v)}
            size="small"
          >
            <Option value="completed">Tamamlandı</Option>
            <Option value="timeout">Timeout</Option>
            <Option value="failed">Başarısız</Option>
            <Option value="pending">Bekliyor</Option>
            <Option value="queued">Kuyrukta (offline)</Option>
          </Select>
        </Col>
        <Col span={4}>
          <Input
            placeholder="Komut adı"
            allowClear
            value={filters.command}
            onChange={(e) => handleFilterChange('command', e.target.value)}
            size="small"
          />
        </Col>
        <Col span={4}>
          <Select
            value={filters.days}
            onChange={(v) => handleFilterChange('days', v)}
            style={{ width: '100%' }}
            size="small"
          >
            <Option value={1}>Son 1 gün</Option>
            <Option value={7}>Son 7 gün</Option>
            <Option value={14}>Son 14 gün</Option>
            <Option value={30}>Son 30 gün</Option>
          </Select>
        </Col>
        <Col span={8}>
          <Button
            icon={<FilterOutlined />}
            onClick={() => {
              setFilters({ command_type: null, status: null, command: null, days: 7 });
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            size="small"
          >
            Filtreleri Temizle
          </Button>
        </Col>
      </Row>

      {/* Table */}
      <Table
        dataSource={history}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
          pageSizeOptions: ['10', '20', '50', '100']
        }}
        onChange={handleTableChange}
        size="small"
        scroll={{ x: 900 }}
      />

      {/* Detail Modal */}
      <Modal
        title="Komut Detayları"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedRecord && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Komut ID">
              <Text code>{selectedRecord.command_id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Cihaz">
              {selectedRecord.device_name} ({selectedRecord.device_mac})
            </Descriptions.Item>
            <Descriptions.Item label="Tip">
              <Tag color={selectedRecord.command_type === 'control' ? 'blue' : 'green'}>
                {selectedRecord.command_type}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Komut">
              <Text code>{selectedRecord.command}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Parametreler">
              <pre style={{ margin: 0, fontSize: '11px', maxHeight: 100, overflow: 'auto' }}>
                {JSON.stringify(selectedRecord.params, null, 2)}
              </pre>
            </Descriptions.Item>
            <Descriptions.Item label="Durum">
              <Space>
                {getStatusIcon(selectedRecord.status)}
                <Tag color={getStatusColor(selectedRecord.status)}>{selectedRecord.status}</Tag>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Kullanıcı">
              {selectedRecord.username || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Gönderim Zamanı">
              {new Date(selectedRecord.sent_at).toLocaleString('tr-TR')}
            </Descriptions.Item>
            {selectedRecord.completed_at && (
              <Descriptions.Item label="Tamamlanma Zamanı">
                {new Date(selectedRecord.completed_at).toLocaleString('tr-TR')}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Timeout">
              {selectedRecord.timeout_seconds}s
            </Descriptions.Item>
            {selectedRecord.response_data && (
              <Descriptions.Item label="Yanıt Verisi">
                <pre style={{ margin: 0, fontSize: '11px', maxHeight: 150, overflow: 'auto' }}>
                  {JSON.stringify(selectedRecord.response_data, null, 2)}
                </pre>
              </Descriptions.Item>
            )}
            {selectedRecord.error_message && (
              <Descriptions.Item label="Hata Mesajı">
                <Text type="danger">{selectedRecord.error_message}</Text>
              </Descriptions.Item>
            )}
            {selectedRecord.batch_id && (
              <Descriptions.Item label="Batch ID">
                <Text code style={{ fontSize: '11px' }}>{selectedRecord.batch_id}</Text>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </Card>
  );
};

export default CommandHistoryTable;
